/*
  Gift card page: updates the card preview as options change, switches on Shopify's recipient sending once an
  email is entered, and adds to the cart (opening the drawer when it's turned on).
*/
if (!customElements.get('sok-gift')) {
  class SokGift extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-sok-gift-form]');
      this.card = this.querySelector('[data-sok-gift-card]');
      this.cta = this.querySelector('[data-sok-gift-cta]');
      this.error = this.querySelector('[data-sok-gift-error]');
      this.form.addEventListener('change', (event) => this.onChange(event));
      this.form.addEventListener('input', (event) => {
        if (event.target.matches('[data-sok-gift-to], [data-sok-gift-email]')) this.sync();
      });
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.sync();
    }

    money(cents) {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: this.dataset.currency || 'AUD',
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
      }).format(cents / 100);
    }

    get variant() {
      return this.form.querySelector('input[name="id"]:checked');
    }

    onChange(event) {
      if (event.target.name === 'id' || event.target.name === 'properties[Design]') this.bump();
      this.sync();
    }

    bump() {
      this.card.classList.remove('is-bumping');
      void this.card.offsetWidth;
      this.card.classList.add('is-bumping');
    }

    sync() {
      const variant = this.variant;
      const price = Number(variant?.dataset.price || 0);
      const design = this.form.querySelector('input[name="properties[Design]"]:checked');
      if (design) {
        [...this.card.classList].filter((c) => c.startsWith('sok-gift__card--')).forEach((c) => this.card.classList.remove(c));
        this.card.classList.add(`sok-gift__card--${design.dataset.design}`);
      }
      this.querySelector('[data-sok-gift-amount]').textContent = this.money(price);
      const to = this.querySelector('[data-sok-gift-to]')?.value.trim();
      this.querySelector('[data-sok-gift-for]').textContent = to ? this.dataset.forLabel.replace('[name]', to) : this.dataset.defaultFor;
      if (!this.busy) this.querySelector('[data-sok-gift-cta-label]').textContent = `${this.dataset.addLabel} · ${this.money(price)}`;

      // Shopify only sends to a recipient when this property is present, and then requires a valid email.
      const email = this.querySelector('[data-sok-gift-email]');
      const sending = Boolean(email?.value.trim());
      const send = this.querySelector('[data-sok-gift-send]');
      const offset = this.querySelector('[data-sok-gift-offset]');
      if (send) send.disabled = !sending;
      if (offset) {
        offset.disabled = !sending;
        offset.value = new Date().getTimezoneOffset();
      }
      if (email) email.required = sending;
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.busy) return;
      this.error.hidden = true;
      const email = this.querySelector('[data-sok-gift-email]');
      if (email && email.value.trim() && !email.checkValidity()) {
        email.reportValidity();
        return;
      }
      this.busy = true;
      const label = this.querySelector('[data-sok-gift-cta-label]');
      label.innerHTML =
        '<svg class="sok-spinner" width="22" height="22" viewBox="0 0 18 18" aria-hidden="true"><circle cx="9" cy="9" r="7" fill="none" stroke="currentColor" stroke-opacity=".25" stroke-width="2"/><path d="M9 2a7 7 0 0 1 7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

      const drawer = document.querySelector('cart-drawer');
      const data = new FormData(this.form);
      // Keep the order tidy: no empty properties, and no recipient details when the card goes to the buyer.
      const sending = Boolean(email?.value.trim());
      const recipientKeys = ['Recipient name', 'Recipient email', 'Send on', 'Message', 'From'].map((k) => `properties[${k}]`);
      [...data.keys()].forEach((key) => {
        if (!key.startsWith('properties[')) return;
        if (!String(data.get(key)).trim() || (!sending && recipientKeys.includes(key))) data.delete(key);
      });
      const sections = ['cart-icon-bubble'];
      if (drawer?.getSectionsToRender) drawer.getSectionsToRender().forEach((x) => sections.push(x.id));
      data.append('sections', [...new Set(sections)].join(','));
      data.append('sections_url', window.location.pathname);
      const started = Date.now();

      try {
        const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: data,
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          // Recipient errors come back keyed by field; show the first message.
          const errors = result.errors && typeof result.errors === 'object' ? Object.values(result.errors).flat() : [];
          throw new Error(errors[0] || result.description || this.dataset.errorLabel);
        }
        await new Promise((r) => setTimeout(r, Math.max(0, 750 - (Date.now() - started))));
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && result.sections?.['cart-icon-bubble']) {
          const doc = new DOMParser().parseFromString(result.sections['cart-icon-bubble'], 'text/html');
          bubble.innerHTML = doc.querySelector('.shopify-section')?.innerHTML ?? doc.body.innerHTML;
        }
        if (drawer?.renderContents) drawer.renderContents(result);
        this.cta.classList.add('is-done');
        label.innerHTML = `<svg class="sok-tick" width="22" height="22" viewBox="0 0 18 18" aria-hidden="true"><circle cx="9" cy="9" r="8" fill="var(--sok-stone)"/><path d="M5.2 9.3l2.5 2.5 5-5.3" fill="none" stroke="var(--sok-success)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg><span class="sok-fade-in">${this.dataset.addedLabel}</span>`;
        setTimeout(() => {
          this.cta.classList.remove('is-done');
          this.busy = false;
          this.sync();
        }, 2000);
        return;
      } catch (e) {
        this.error.textContent = e.message || this.dataset.errorLabel;
        this.error.hidden = false;
      }
      this.busy = false;
      this.sync();
    }
  }

  customElements.define('sok-gift', SokGift);
}
